import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useContentId } from '@/components/SlugGate';
import { sanitizeHtml } from '@/lib/sanitize';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Heart, Calendar, Share2, MessageCircle, Loader2 } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { confessionJsonLd } from '@/lib/jsonLd';

export default function ConfessionDetail() {
  const id = useContentId();
  const navigate = useNavigate();
  const [confession, setConfession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/confessions/' + id);
        setConfession(res.data);
      } catch (error) {
        toast.error('Confissão não encontrada');
        navigate('/confessions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: confession.title, url });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado!');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  if (!confession) return null;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={confession.title}
        description={confession.excerpt}
        type="article"
        jsonLd={confessionJsonLd(confession)}
      />
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 pt-24 pb-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/confessions')}
          className="mb-6 text-muted-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar às Confissões
        </Button>

        <Card className="bg-surface border-border">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                {confession.category && (
                  <Badge className="bg-ruby mb-3">{confession.category}</Badge>
                )}
                <h1 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="confession-title">
                  {confession.title}
                </h1>
              </div>
              <Button variant="outline" size="icon" onClick={handleShare} className="border-border shrink-0">
                <Share2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8 pb-6 border-b border-border">
              <span className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                {confession.author_name || 'Anónimo'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(confession.published_at || confession.submitted_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {confession.views || 0} visualizações
              </span>
            </div>

            <article data-testid="confession-content">
              <div className="reader-content text-muted-foreground text-lg" style={{ lineHeight: '1.9' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(confession.content) }} />
            </article>

            {/* Post-reading conversion block — reuses existing /confess flow */}
            <div className="mt-10 pt-8 border-t border-border" data-testid="confession-post-cta">
              <div className="bg-ruby/10 border border-ruby/30 rounded-xl px-6 py-8 text-center">
                <p className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                  E tu, o que nunca contaste a ninguém?
                </p>
                <p className="text-sm text-muted-foreground mb-5">
                  A tua identidade não será mostrada aos outros leitores.
                </p>
                <Button
                  onClick={() => navigate('/confess')}
                  className="bg-ruby hover:bg-ruby-light px-6 py-5"
                  data-testid="confession-detail-cta-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Fazer uma confissão
                </Button>
              </div>
              <div className="flex justify-center mt-4">
                <Button variant="ghost" onClick={() => navigate('/confessions')} className="text-muted-foreground">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Ver mais confissões
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
